import { isStepApplicable, isStepComplete } from '../registry';

/**
 * Move the linear step pointer to the first step that still needs the user.
 *
 * Forward pass, from the pointer on:
 * - steps on a page already left behind, or on a page this wizard never
 *   shows, are **skipped** for good;
 * - a step on a later page stops the pass (wait for Next);
 * - a step that does not apply right now is **deferred**: passed over, but it
 *   comes back (see below) should the wizard state make it apply again while
 *   the user is still on its page;
 * - a step whose action is already done is recorded as **completed**;
 * - the step at `holdIndex` stops the pass even when its action looks done.
 *   The runner holds the step a resumed tour stopped at until the user acts
 *   on it, so that values the wizard restored from a draft (the default
 *   fallback label, the single/multi default) are not mistaken for choices
 *   the user made and the tour reopens exactly where it was left.
 *
 * Revisit, before the forward pass: the earliest step on the current page
 * that was deferred earlier (`deferredIds`), lies before the pointer, applies
 * now, is not done and was neither skipped nor already passed with Next by
 * the user (`skippedIds` / `completedIds`) wins the pointer back. This is
 * what shows the label-priority mark when a second label turns the fallback
 * picker into "What matters most for these labels?". Guide marks have no
 * completion predicate, so the `completedIds` check is what keeps a revisited
 * one from coming back after the user moved on from it.
 *
 * Pure: returns the new pointer plus the ids that were auto-skipped /
 * auto-completed / deferred so the caller can record them. `deferred` also
 * lists the steps before the pointer on the current page that do not apply
 * right now, so a resumed tour tracks them without having walked over them.
 */
export function resolvePointer({
    steps,
    stepIndex,
    ctx,
    currentPage,
    pageOrder = [],
    deferredIds = [],
    skippedIds = [],
    completedIds = [],
    holdIndex = -1,
}) {
    const skipped = [];
    const completed = [];
    const deferred = [];
    const currentPageIdx = pageOrder.indexOf(currentPage);
    let index = Math.max(0, stepIndex);

    if (currentPageIdx !== -1) {
        const revisit = steps.findIndex(
            (step, i) =>
                i < index &&
                step.wizardStep === currentPage &&
                deferredIds.includes(step.id) &&
                !skippedIds.includes(step.id) &&
                !completedIds.includes(step.id) &&
                isStepApplicable(step, ctx) &&
                !isStepComplete(step, ctx)
        );
        if (revisit !== -1) index = revisit;
        steps.forEach((step, i) => {
            if (i < index && step.wizardStep === currentPage && !isStepApplicable(step, ctx)) deferred.push(step.id);
        });
    }

    while (index < steps.length) {
        const step = steps[index];
        const stepPageIdx = pageOrder.indexOf(step.wizardStep);

        if (stepPageIdx === -1) {
            // This wizard never shows the page the step lives on.
            skipped.push(step.id);
            index += 1;
            continue;
        }
        if (currentPageIdx === -1) break; // page unknown yet: wait
        if (stepPageIdx < currentPageIdx) {
            // The user moved past this page before the step was done.
            skipped.push(step.id);
            index += 1;
            continue;
        }
        if (stepPageIdx > currentPageIdx) break; // later page: wait for Next

        if (!isStepApplicable(step, ctx)) {
            deferred.push(step.id);
            index += 1;
            continue;
        }
        if (index === holdIndex) break; // resumed here: wait for the user even if it looks done
        if (isStepComplete(step, ctx)) {
            completed.push(step.id);
            index += 1;
            continue;
        }
        break;
    }

    return { stepIndex: index, skipped, completed, deferred };
}

/**
 * Steps counted in "Step N/Total": those on a page this wizard shows and
 * applicable under the current context. Skipped steps stay counted so the
 * numbering never jumps backwards.
 */
export function applicableSteps({ steps, ctx, pageOrder }) {
    return steps.filter(
        (step) => (!pageOrder || pageOrder.includes(step.wizardStep)) && isStepApplicable(step, ctx)
    );
}
